import {
  toWSDate,
  WSElement,
  WSEmptyState,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPageToolbarAction,
  WSPanel,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  PayeeRequirement,
  PayeeEngagementStatus,
  PayeeIdentitySource,
  PayeeStatus,
  PayerLinkRequestStatus
} from "@wingspanhq/wingspan-api-sdk";
import React, { useState } from "react";
import { Route, RouteComponentProps, Switch } from "react-router-dom";
import {
  queriesAccounts,
  queriesRequirements,
  queriesPayeeEngagements,
  queriesPayees
} from "@query";
import { EngagementsTable } from "./EngagementsTable";
import { useSession } from "@hooks/useSession";
import { Tabs } from "../../../../components/Tabs";
import { WSQueries } from "../../../../query/WSQuery";
import { useGoTo } from "../../../../shared/utils/useGoTo";
import { ContractorInformation } from "../../components/ContractorInformation";
import { CustomFieldsWidget } from "../../components/CustomFieldsWidget/CustomFieldsWidget";
import { InternalNotesWidget } from "../../components/InternalNotesWidget";
import { ResourceHistory } from "../../../../shared/components/ResourceHistory";
import { selectorAccountDisplayName } from "../../../../shared/selectors/selectorAccountDisplayName";
import { selectorCounterpartyDisplayName } from "../../../../shared/selectors/selectorCounterpartyDisplayName";
import { usePayeeHistoryResources } from "../../hooks/usePayeeHistoryResources";
import {
  buildPayeeDetailsPath,
  buildPayeeEditTaxInfoPath,
  buildPayeeHistoryPath,
  PAYEES_ARCHIVED_PATH,
  PAYEES_DETAILS_ROUTE,
  PAYEES_LIST_PATH
} from "../../utils";
import { PayeeIntegrationWidget } from "../../../Payers/components/IntegrationsSection/PayeeIntegrationWidget";
import { PayeeDetailsAlerts } from "./PayeeDetailsAlerts";
import { UnavailableOrgWidget } from "./UnavailableOrgWidget";
import { usePayeeRowActions } from "./usePayeeRowActions";
import { useXWingspanPayerId } from "./useXWingspanPayerId";

// ============================================================================
// v3 migration — accepted breakage (directive: "not a single v1 endpoint may be
// called on the contractor-details screen"; user-confirmed 2026-07-22).
//
// Everything this screen READS (`getPayee`, org `getAccount`) and the header
// WRITES (edit / archive / restore / invite / internal notes) is on v3. The
// reads + widgets commented out below hit v1 endpoints whose data has NO usable
// v3 source today:
//   - Requirements tab  → removed with the hub cutover; requirement instances
//                         are on v3 now (payee-requirements), so restoring the
//                         tab is a product decision, not a blocker.
//   - TaxFilingWidget   → v1 /search/tax-form-submission-row (no v3 search-row
//                         with the derived filing status).
//   - RecentPayables    → v1 /payments/payable + /payroll-settings (payroll
//                         settings drive the status column and have no v3 twin).
//   - DeductionsWidget  → v1 /payments/collaborator-deduction (no v3).
//   - DocumentsSection  → v1 /files/member/private (no v3 files registry).
//   - AutopayWidget     → v1 /user/client/:id for the payer's default payment
//                         method; the widget renders nothing without it.
//
// RESTORED since: the QuickBooks mapping panel (`PayeeIntegrationWidget`) is back
// on v3 — `listAccountingConnections` + `listAccountingVendors` +
// `listLedgerAccounts` for the reference data, and the mapped ids off
// `Payee.quickbooksVendorId` / `.quickbooksExpenseAccountId`.
//
// Each removed panel already degraded to empty / non-functional on a v3 payee
// (its data never resolved), so commenting them out is not a functional
// regression — it only stops the dead v1 traffic. Preserved as comments for
// re-enablement once a v3 source ships. The app-wide experiments/feature-flags
// dependency reached via useXWingspanPayerId was also removed at the source
// (see useXWingspanExpansion). See V3_MIGRATION_TRACKER.md · /v3/payments/payees.
// ============================================================================

export const RoutePayeeDetails: React.FC<
  RouteComponentProps<{
    payeeId: string;
  }>
> = ({ match, history, location }) => {
  const goTo = useGoTo();
  const { payeeId } = match.params;

  const organizationId = useXWingspanPayerId();

  const isOrgContext = !!organizationId;
  const getPayeeActions = usePayeeRowActions();
  const historyResources = usePayeeHistoryResources(payeeId);

  const { personId } = useSession();
  const queryPayee = queriesPayees.getPayee.useQuery({
    payeeId,
    xWingspanAccount: organizationId
  });
  // Org breadcrumb name (org-expansion context only). v3 read: getAccount →
  // selectorAccountDisplayName (was v1 GET /organization/user/:id).
  const queryOrganization = queriesAccounts.getAccount.useQuery(
    { accountId: organizationId! },
    { enabled: isOrgContext && organizationId !== personId }
  );

  // Assignments (payee-engagements) + their eligibility requirements, on v3.
  // Unblocked by WIN-15176 (paymentsEligibility now on the projection) and
  // WIN-15163 (payee-eligibility-requirements 403 resolved).
  const [showArchived, setShowArchived] = useState<boolean>(false);
  // `useQueryAll`, not `useQuery`: the endpoint is paginated, so a single page
  // silently truncates the tables below — a newly assigned engagement that lands
  // beyond the first page would never render. Every other consumer of this list
  // (incl. the create modal, which is why it can hide an assignment the tables
  // never showed) pages to completion too.
  //
  // TODO(v3): this should be `usePaginatedQuery` — pagination is preferred for
  // any table (see `docs/v3-queries.md` · "Prefer pagination"). It is not
  // workable yet: the tables split one list into active/archived buckets, but
  // `listPayeeEngagements`'s filter exposes only `externalId` (no `status`), so
  // paging the combined list would leave each bucket with an arbitrary slice of
  // every page. Its `pagination` also returns only `nextPageToken` — no
  // `totalSize` — so no page count can be rendered. Verified against the live
  // API on SDK 3.11.1. Revisit once BE adds `filter.status` + `totalSize` here.
  const queryEngagements =
    queriesPayeeEngagements.listPayeeEngagements.useQueryAll({
      payeeId,
      xWingspanAccount: organizationId
    });
  // The list filters on the payee's Account id, not the Payee resource id.
  const payeeAccountId = queryPayee.data?.payeeAccountId;
  const queryEligibilityRequirements =
    queriesRequirements.listPayeeRequirements.useQuery(
      { filter: { payeeAccountId: { anyOf: [payeeAccountId as string] } } },
      { enabled: !!payeeAccountId }
    );

  return (
    <WSPage>
      <WSQueries
        queries={{
          queryPayee
        }}
      >
        {({ queryPayeeData }) => {
          // `isInitialLoading`, not `isLoading`: react-query v4 parks a *disabled*
          // query in `status: "loading"` forever, so
          // `queryEligibilityRequirements.isLoading` never goes false for a payee
          // with no `payeeAccountId` — the section shimmered indefinitely.
          const secondaryLoading =
            queryEngagements.isInitialLoading ||
            queryEligibilityRequirements.isInitialLoading;
          const engagements = queryEngagements.flatData ?? [];
          const requirements = queryEligibilityRequirements.data?.data ?? [];
          // Group eligibility requirements by engagement for the per-row
          // "X of Y complete" count (v3 `PayeeEngagement` carries only the
          // `requirementIds` + `areAllRequirementsComplete` rollup).
          // One instance can now satisfy several engagements, so it counts
          // towards each one it is attached to.
          const requirementsByEngagementId = requirements.reduce<
            Record<string, PayeeRequirement[]>
          >((acc, requirement) => {
            (requirement.payeeEngagementIds ?? []).forEach(engagementId => {
              (acc[engagementId] ??= []).push(requirement);
            });
            return acc;
          }, {});
          const activeEngagements = engagements.filter(
            eng =>
              eng.status === PayeeEngagementStatus.CREATED ||
              eng.status === PayeeEngagementStatus.ACTIVATED
          );
          const archivedEngagements = engagements.filter(
            eng =>
              eng.status === PayeeEngagementStatus.SUSPENDED ||
              eng.status === PayeeEngagementStatus.TERMINATED
          );

          // Named off the `Payee` entity, not the search row: the row carries
          // the server-resolved `profile.resolvedName`, but making the whole
          // detail page depend on the search index means an unavailable index
          // takes the page down with it. `selectorCounterpartyDisplayName`
          // falls back through displayName → company DBA → email → id.
          const payeeName = selectorCounterpartyDisplayName(queryPayeeData);

          const isInactive = queryPayeeData.status === PayeeStatus.DEACTIVATED;

          // `identitySource` is the correct ownership axis for payer-provided
          // vs contractor-shared: a `PayeeOwned` identity is contractor-shared
          // and the payer cannot edit it, while `PayerProvided` is "provided by
          // you" and editable.
          const isPayeeSharedInfo =
            queryPayeeData.identitySource === PayeeIdentitySource.PAYEE_OWNED;

          const headerActions: WSPageToolbarAction[] = getPayeeActions({
            payee: {
              payeeId,
              email: queryPayeeData.email,
              internalNotes: queryPayeeData.internalNotes ?? undefined,
              isInactive,
              isPending:
                queryPayeeData.linkRequestStatus ===
                PayerLinkRequestStatus.PENDING,
              isPayeeProvidedInfo: isPayeeSharedInfo
            },
            backPath: location.pathname
          });

          const organizationName = queryOrganization.data
            ? selectorAccountDisplayName(queryOrganization.data)
            : undefined;

          return (
            <>
              <WSPageToolbar
                breadcrumbs={[
                  {
                    label: "Contractors",
                    onClick: () => {
                      history.push(PAYEES_LIST_PATH);
                    }
                  },
                  ...(organizationName
                    ? [
                        {
                          label: organizationName,
                          onClick: () => {
                            history.push(
                              PAYEES_LIST_PATH +
                                `?payerId%5B0%5D=${organizationId}`
                            );
                          }
                        }
                      ]
                    : []),
                  ...(isInactive
                    ? [
                        {
                          label: "Archived contractors",
                          onClick: () => {
                            history.push(PAYEES_ARCHIVED_PATH);
                          }
                        }
                      ]
                    : []),
                  { label: payeeName }
                ]}
                title={payeeName}
                titlePill={
                  isInactive
                    ? {
                        theme: "neutral",
                        text: "Archived",
                        icon: false
                      }
                    : queryPayeeData.linkRequestStatus ===
                        PayerLinkRequestStatus.LINKED
                      ? { theme: "success", text: "Signed-up", icon: false }
                      : {
                          theme: "warning",
                          text: "Not signed-up",
                          icon: false
                        }
                }
                titleAfter={
                  <WSText.ParagraphXs color="gray500">
                    Last modified{" "}
                    {toWSDate(queryPayeeData.events?.updatedAt).formattedValue}
                  </WSText.ParagraphXs>
                }
                actions={headerActions}
              />
              <WSList gap="L">
                <PayeeDetailsAlerts payeeId={payeeId} />

                <WSGrid gutter="XL">
                  <WSGrid.Item span={{ m: "8" }}>
                    <Tabs
                      mb="L"
                      tabs={[
                        {
                          label: "Summary",
                          path: buildPayeeDetailsPath(payeeId)
                        }
                        // v3-migration accepted breakage: the Requirements tab
                        // was removed with the hub cutover. Requirement instances
                        // are on v3 now, so restoring it is a product decision.
                        // Commented out with the requirements route below.
                        // {
                        //   label: "Requirements",
                        //   pill: {
                        //     icon: true,
                        //     theme: isAllRequirementsComplete
                        //       ? "success"
                        //       : "warning"
                        //   },
                        //   path: buildPayeeDetailsRequirementsPath(payeeId)
                        // }
                      ]}
                    />

                    <Switch>
                      {/*
                        v3-migration accepted breakage: the Requirements route
                        (PayerPayeeDetailsRequirements) was removed with the hub
                        cutover. Requirement instances are on v3 now — restoring
                        the tab is a product decision. Commented out with the tab
                        above.

                        <Route
                          path={PAYEES_DETAILS_REQUIREMENTS_ROUTE}
                          render={() => (
                            <WSElement shimmer={tertiaryLoading}>
                              <PayerPayeeDetailsRequirements
                                requirements={requirements}
                                hasEligibleEngagements={hasEligibleEngagements}
                                noActions={isOrgContext}
                                archivedRequirements={queryArchivedRequirements.data || []}
                                contextType={RequirementContextType.Payee}
                                status={isInactive ? PayerPayeeStatus.Inactive : PayerPayeeStatus.Active}
                                goToRequirement={requirement =>
                                  history.push(
                                    `/member/payees/${payeeId}/requirement/${requirement.requirementId}`
                                  )
                                }
                              />
                            </WSElement>
                          )}
                        />
                      */}
                      <Route
                        path={PAYEES_DETAILS_ROUTE}
                        render={() => (
                          <WSList gap="XL">
                            <WSElement>
                              <WSSectionToolbar
                                pill={{
                                  text: isPayeeSharedInfo
                                    ? "Contractor shared"
                                    : "Provided by you",
                                  theme: isPayeeSharedInfo ? "blue" : "neutral"
                                }}
                                title="Contractor information"
                                button={
                                  isPayeeSharedInfo
                                    ? undefined
                                    : {
                                        label: "Edit",
                                        onClick: () => {
                                          goTo(
                                            buildPayeeEditTaxInfoPath(payeeId)
                                          );
                                        }
                                      }
                                }
                              />
                              <WSPanel mt="S">
                                <WSList gap="2XL">
                                  <ContractorInformation
                                    payeeId={payeeId}
                                    editable
                                  />
                                </WSList>
                              </WSPanel>
                            </WSElement>
                            <WSList gap="L">
                              {isInactive ? null : (
                                <WSElement shimmer={secondaryLoading}>
                                  <WSSectionToolbar
                                    title="Assignments"
                                    mb="M"
                                  />
                                  {activeEngagements.length ? (
                                    <EngagementsTable
                                      engagements={activeEngagements}
                                      requirementsByEngagementId={
                                        requirementsByEngagementId
                                      }
                                      payee={queryPayeeData}
                                    />
                                  ) : (
                                    <WSEmptyState
                                      type="tasks"
                                      title="No assignments active"
                                      description="Once an assignment is added and active, it will appear here."
                                      hasBorder
                                      orientation="horizontal"
                                    />
                                  )}
                                </WSElement>
                              )}
                              {archivedEngagements.length ? (
                                <WSElement>
                                  <WSSectionToolbar
                                    title={
                                      showArchived || isInactive
                                        ? "Archived assignments"
                                        : ""
                                    }
                                    mb="M"
                                    buttons={
                                      isInactive
                                        ? undefined
                                        : [
                                            {
                                              label: showArchived
                                                ? `Hide archived (${archivedEngagements.length})`
                                                : `Show archived (${archivedEngagements.length})`,
                                              onClick: () => {
                                                setShowArchived(v => !v);
                                              }
                                            }
                                          ]
                                    }
                                  />
                                  {isInactive || showArchived ? (
                                    <EngagementsTable
                                      engagements={archivedEngagements}
                                      requirementsByEngagementId={
                                        requirementsByEngagementId
                                      }
                                      payee={queryPayeeData}
                                    />
                                  ) : null}
                                </WSElement>
                              ) : null}
                            </WSList>
                          </WSList>
                        )}
                      />
                    </Switch>
                  </WSGrid.Item>
                  <WSGrid.Item span={{ m: "4" }}>
                    <WSList gap="XL">
                      <InternalNotesWidget payeeId={payeeId} />

                      <ResourceHistory
                        resources={historyResources}
                        xWingspanAccount={organizationId}
                        onViewAll={() => {
                          history.push(buildPayeeHistoryPath(payeeId));
                        }}
                      />

                      <CustomFieldsWidget
                        payeeId={payeeId}
                        isInactive={isInactive}
                      />

                      {/*
                        v3-migration accepted breakage — commented out to keep
                        this screen off v1. Each widget's data has no usable v3
                        source today (see the banner at the top of this file):

                        {hasScope("payments.payable")
                          ? isOrgContext
                            ? null
                            : <RecentPayablesWidget payeeId={payeeId} />
                          : null}

                        <TaxFilingWidget payeeId={payeeId} />

                        {isOrgContext ? null : (
                          <WSElement shimmer={secondaryLoading}>
                            <ESignatureWidget payeeId={payeeId} />
                          </WSElement>
                        )}

                        <DocumentsSection
                          isInactive={isInactive}
                          payeeId={payeeId}
                          payerId={userId}
                          type={DocumentsSectionType.Collaborator}
                        />

                        {isOrgContext ? null : (
                          <DeductionsWidget
                            clientId={userId}
                            memberId={payeeId}
                            onRowClick={row => {
                              history.push(
                                `/member/invoices/payables/deductions/scheduled/${row.deductionId}`
                              );
                            }}
                            onShowAllClick={() => {
                              history.push(`/member/invoices/payables/deductions/scheduled`);
                            }}
                          />
                        )}

                        <AutopayWidget isInactive={isInactive} payeeId={payeeId} />
                      */}

                      {/*
                        Restored on v3. The contractor-LEVEL QuickBooks mapping is
                        readable: `Payee.quickbooksVendorId` and
                        `.quickbooksExpenseAccountId` are both on the v3 model, and
                        `PayeeIntegrationWidget` now reads its reference data from
                        `listAccountingConnections` + `listAccountingVendors` +
                        `listLedgerAccounts`. (The ASSIGNMENT-level mapping is still
                        write-only — `PayeeEngagement` exposes neither field — which
                        is why the widget on the assignment screen is fed undefined.)
                      */}
                      {isOrgContext ? null : (
                        <PayeeIntegrationWidget
                          isInactive={isInactive}
                          payeeId={payeeId}
                          vendorId={
                            queryPayeeData.quickbooksVendorId ?? undefined
                          }
                          expenseAccountId={
                            queryPayeeData.quickbooksExpenseAccountId ??
                            undefined
                          }
                        />
                      )}

                      {isOrgContext ? (
                        <>
                          <UnavailableOrgWidget title="Recent payables" />
                          <UnavailableOrgWidget title="Scheduled deductions" />
                          <UnavailableOrgWidget title="Integration mappings" />
                        </>
                      ) : null}
                    </WSList>
                  </WSGrid.Item>
                </WSGrid>
              </WSList>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
